import React from 'react';
import {
  Film, Sparkles, Video, Images, FileVideo, Mail, ArrowRight,
  CalendarCheck, MessageSquare, FolderOpen, CheckCircle2,
} from 'lucide-react';
import { usePortalScope } from '../../context/PortalScopeContext';

// /portal/media for schools that have NEVER held a media package.
//
// The Media tab is shown to every school (DashboardLayout no longer gates it).
// MediaCalendar renders this info/upsell page when the account has no package
// rows; the moment a media invoice is signed, the sign-hook inserts an active
// media_packages row, so the next portal load swaps this out for the live
// booking calendar automatically — no flag, no manual step.
//
// Content is sourced from confirmed facts only (pricing, tiers, 12-month
// validity, the three per-event deliverables, the no-fee DOE policy). It makes
// no claims the booking system can't back up.

const TIERS = [
  {
    name: 'Standard',
    price: '$7,500',
    events: 5,
    blurb: 'Five events of professional coverage across your year.',
    featured: false,
  },
  {
    name: 'Premium',
    price: '$9,500',
    events: 7,
    blurb: 'Seven events, the best value per event for a fuller year of coverage.',
    featured: true,
  },
];

const DELIVERABLES = [
  {
    icon: Video,
    title: 'Edited highlight reel',
    body: 'A polished 1–3 minute video of the event, ready to share.',
  },
  {
    icon: Images,
    title: 'Curated photo gallery',
    body: '30–80 edited photos, sized for your website and socials.',
  },
  {
    icon: FileVideo,
    title: 'Raw footage to keep',
    body: 'All the original video files, delivered to you. Yours forever.',
  },
];

const STEPS = [
  { icon: CalendarCheck, title: 'Book an event', body: 'Pick a date and tell us about the moment, right here in your portal.' },
  { icon: CheckCircle2, title: 'We schedule it', body: 'Our team reviews every request and schedules the shoot, handled by a real person on our side.' },
  { icon: MessageSquare, title: 'Plan the shoot', body: 'An optional quick planning call before the day, so we capture what matters.' },
  { icon: FolderOpen, title: 'Delivered', body: 'Your reel, gallery, and raw files arrive in a shared Google Drive folder.' },
];

export default function MediaPackageInfo() {
  const { account } = usePortalScope();
  const subject = encodeURIComponent(
    `Media package inquiry${account?.name ? `: ${account.name}` : ''}`,
  );
  const jerrellHref = `mailto:jerrell@nexsav.net?subject=${subject}`;
  const supportHref = `mailto:support@nexsav.net?subject=${subject}`;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-white tracking-tight">Media <span className="serif-acc accent-text">Package</span></h1>
        <p className="text-sm text-gray-500 mt-1">
          Professional photo &amp; video coverage of your school, booked and tracked right here.
        </p>
      </div>

      {/* Hero */}
      <div className="rounded-3xl border border-[#B0FC38]/25 bg-gradient-to-br from-[#B0FC38]/[0.08] via-white/[0.02] to-[#B0FC38]/[0.04] p-8 md:p-10 text-center">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-[#B0FC38]/15 border border-[#B0FC38]/30 flex items-center justify-center mb-5">
          <Film className="w-6 h-6 text-[#B0FC38]" />
        </div>
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#B0FC38]/[0.08] border border-[#B0FC38]/25 text-[11px] uppercase tracking-wider text-[#B0FC38] mb-4">
          <Sparkles className="w-3 h-3" />
          Add-on
        </div>
        <h2 className="text-2xl font-semibold text-white tracking-tight">
          Capture the moments that <span className="serif-acc accent-text">matter</span>
        </h2>
        <p className="text-[14px] text-gray-300 leading-relaxed mt-3 max-w-md mx-auto">
          Book professional coverage for performances, ceremonies, game days, and
          milestones. Every event comes back as a polished highlight reel, a
          curated photo gallery, and the raw footage, yours to keep.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-7">
          <a
            href={jerrellHref}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 transition-all"
          >
            <Mail className="w-4 h-4" />
            Talk to our team
          </a>
          <a
            href={supportHref}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white hover:border-white/[0.2] transition-all"
          >
            Email support
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {/* Pricing tiers */}
      <div className="mt-8">
        <h2 className="text-[13px] font-medium text-white tracking-tight mb-3">Choose your coverage</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {TIERS.map((tier) => (
            <div
              key={tier.name}
              className={`relative rounded-2xl p-5 ${
                tier.featured
                  ? 'border border-[#B0FC38]/30 bg-[#B0FC38]/[0.04]'
                  : 'border border-white/[0.06] bg-white/[0.02]'
              }`}
            >
              {tier.featured && (
                <span className="absolute top-4 right-4 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#B0FC38]/[0.12] border border-[#B0FC38]/30 text-[#B0FC38]">
                  Best value
                </span>
              )}
              <div className="text-[13px] text-gray-400 font-medium">{tier.name}</div>
              <div className="flex items-baseline gap-2 mt-1.5">
                <span className="text-3xl font-bold text-white">{tier.price}</span>
                <span className="text-[12px] text-gray-500">one-time</span>
              </div>
              <div className="flex items-baseline gap-1.5 mt-2">
                <span className="text-xl font-bold text-[#B0FC38]">{tier.events}</span>
                <span className="text-[12px] text-gray-500">events of coverage</span>
              </div>
              <p className="text-[12px] text-gray-400 leading-relaxed mt-2.5">{tier.blurb}</p>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-gray-500 mt-2.5">
          Events are valid for 12 months from purchase. No booking, rush, or travel
          fees. Every shoot is reviewed and scheduled by our team.
        </p>
      </div>

      {/* Deliverables */}
      <div className="mt-8">
        <h2 className="text-[13px] font-medium text-white tracking-tight mb-3">What you get for every event</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {DELIVERABLES.map((d) => {
            const Icon = d.icon;
            return (
              <div key={d.title} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
                <div className="w-9 h-9 rounded-lg bg-[#B0FC38]/15 border border-[#B0FC38]/30 flex items-center justify-center mb-3">
                  <Icon className="w-4 h-4 text-[#B0FC38]" />
                </div>
                <div className="text-[14px] text-white font-medium">{d.title}</div>
                <p className="text-[12px] text-gray-400 leading-relaxed mt-1">{d.body}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* How it works */}
      <div className="mt-8">
        <h2 className="text-[13px] font-medium text-white tracking-tight mb-3">How it works</h2>
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04]">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="flex items-start gap-3.5 p-4">
                <div className="w-8 h-8 rounded-lg bg-[#B0FC38]/[0.12] border border-[#B0FC38]/30 flex items-center justify-center shrink-0 mt-0.5">
                  <Icon className="w-4 h-4 text-[#B0FC38]" />
                </div>
                <div className="min-w-0">
                  <div className="text-[13px] text-white font-medium">
                    <span className="text-gray-500 mr-1.5">{i + 1}.</span>{s.title}
                  </div>
                  <p className="text-[12px] text-gray-400 leading-relaxed mt-0.5">{s.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Closing CTA */}
      <div className="mt-8 rounded-2xl border border-[#B0FC38]/25 bg-[#B0FC38]/[0.06] p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white">Interested in a media package?</div>
          <p className="text-[12px] text-gray-400 leading-relaxed mt-1">
            Reach out and we&rsquo;ll get you set up. Once your media invoice is signed,
            this tab turns into your live booking calendar automatically.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <a
            href={jerrellHref}
            className="inline-flex items-center gap-1.5 text-[13px] text-[#B0FC38] hover:text-white transition-colors"
          >
            <Mail className="w-3.5 h-3.5" />
            jerrell@nexsav.net
          </a>
          <a
            href={supportHref}
            className="inline-flex items-center gap-1.5 text-[13px] text-gray-400 hover:text-white transition-colors"
          >
            <Mail className="w-3.5 h-3.5" />
            support@nexsav.net
          </a>
        </div>
      </div>
    </div>
  );
}
